import { motion } from "framer-motion";
import { ArrowUpRight, Wallet, Briefcase, BookOpen, HeartPulse, MessageCircle, Sparkles } from "lucide-react";

const IMG_CAREER = "https://images.unsplash.com/photo-1620829813573-7c9e1877706f?auto=format&fit=crop&w=1200&q=80";
const IMG_STUDY = "https://images.unsplash.com/photo-1694175271713-a6e2cc378980?auto=format&fit=crop&w=1200&q=80";
const IMG_YOUTH = "https://images.unsplash.com/photo-1534597422092-8a84f1b45a9c?auto=format&fit=crop&w=1200&q=80";

const fade = { hidden: { opacity: 0, y: 16 }, show: { opacity: 1, y: 0 } };

export default function RegularSections() {
  return (
    <section id="sections" data-testid="regular-sections" className="relative border-t border-[#2E1A6B]/15">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10 md:mb-14">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#2E1A6B] mb-4">
              The Blueprint · Regular Sections
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[0.95] text-[#1A1A1A] max-w-2xl">
              Seven pillars.<br />One generation ready to move.
            </h2>
          </div>
          <p className="text-base md:text-lg text-[#4A4A4A] max-w-md">
            Every issue delivers practical knowledge and real perspective across the topics
            shaping young Ghanaian lives — money, work, study, health, and voice.
          </p>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-0 border border-[#2E1A6B]/20">
          {/* Career Focus - large image */}
          <motion.div
            variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6 }}
            data-testid="section-career-focus"
            className="md:col-span-8 relative min-h-[380px] md:min-h-[480px] border-b md:border-b-0 md:border-r border-[#2E1A6B]/20 overflow-hidden group"
          >
            <img src={IMG_CAREER} alt="Career focus" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-transparent" />
            <div className="relative z-10 h-full flex flex-col justify-between p-6 md:p-10">
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-semibold text-[#2AC1E0]">
                <Briefcase size={14} /> Career Focus
              </div>
              <div>
                <h3 className="font-display text-white text-3xl md:text-5xl leading-[1.05] tracking-tight max-w-xl">
                  From national service to the corner office — real paths, mapped.
                </h3>
                <div className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-semibold text-white">
                  Read section <ArrowUpRight size={16} />
                </div>
              </div>
            </div>
          </motion.div>

          {/* Financial Literacy - cyan block */}
          <motion.div
            variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6, delay: 0.05 }}
            data-testid="section-financial-literacy"
            className="md:col-span-4 relative bg-[#2AC1E0] p-6 md:p-8 border-b md:border-b-0 border-[#2E1A6B]/20 min-h-[380px] md:min-h-[480px] flex flex-col justify-between"
          >
            <div>
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-semibold text-[#2E1A6B]">
                <Wallet size={14} /> Financial Literacy
              </div>
              <h3 className="font-display text-[#2E1A6B] text-3xl md:text-4xl leading-[1.05] tracking-tight mt-6">
                Cedis, savings, side hustles &amp; the money mindset.
              </h3>
              <p className="mt-4 text-[#2E1A6B]/85 text-sm md:text-base leading-relaxed">
                Straight-talk breakdowns of budgeting, investing, and building wealth as a
                student or young professional.
              </p>
            </div>
            <div className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-semibold text-[#2E1A6B]">
              Explore <ArrowUpRight size={16} />
            </div>
          </motion.div>

          {/* Study Smart */}
          <motion.div
            variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            data-testid="section-study-smart"
            className="md:col-span-4 relative min-h-[320px] md:min-h-[420px] border-b md:border-b-0 md:border-r border-[#2E1A6B]/20 overflow-hidden group"
          >
            <img src={IMG_STUDY} alt="Study smart" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#2E1A6B]/85 via-[#2E1A6B]/30 to-transparent" />
            <div className="relative z-10 h-full flex flex-col justify-between p-6 md:p-8">
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-semibold text-[#2AC1E0]">
                <BookOpen size={14} /> Study Smart
              </div>
              <h3 className="font-display text-white text-2xl md:text-3xl leading-tight tracking-tight">
                Study habits that beat the system.
              </h3>
            </div>
          </motion.div>

          {/* Ask the Counsellor - deep indigo */}
          <motion.div
            variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            data-testid="section-ask-counsellor"
            className="md:col-span-4 relative bg-[#2E1A6B] p-6 md:p-8 border-b md:border-b-0 md:border-r border-[#2E1A6B]/20 min-h-[320px] md:min-h-[420px] flex flex-col justify-between overflow-hidden grain"
          >
            <div className="relative z-10">
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-semibold text-[#2AC1E0]">
                <MessageCircle size={14} /> Ask the Counsellor
              </div>
              <h3 className="font-display text-white text-2xl md:text-3xl leading-tight tracking-tight mt-6">
                Honest answers to the questions you don&apos;t ask out loud.
              </h3>
              <p className="mt-4 text-white/70 text-sm leading-relaxed">
                Anonymous letters. Trained counsellors. No judgement.
              </p>
            </div>
            <div className="relative z-10 mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-semibold text-[#2AC1E0]">
              Submit a letter <ArrowUpRight size={16} />
            </div>
          </motion.div>

          {/* Health Matters */}
          <motion.div
            variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            data-testid="section-health-matters"
            className="md:col-span-4 relative bg-[#F4F4F0] p-6 md:p-8 min-h-[320px] md:min-h-[420px] flex flex-col justify-between"
          >
            <div>
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-semibold text-[#2E1A6B]">
                <HeartPulse size={14} /> Health Matters
              </div>
              <h3 className="font-display text-[#1A1A1A] text-2xl md:text-3xl leading-tight tracking-tight mt-6">
                Body, mind, and the truth about wellbeing.
              </h3>
              <p className="mt-4 text-[#4A4A4A] text-sm leading-relaxed">
                Mental health, nutrition, sexual health — evidence-based, no shame.
              </p>
            </div>
            <div className="mt-6 flex items-end justify-between">
              <span className="font-display text-6xl md:text-7xl text-[#2E1A6B]/15 leading-none">05</span>
              <ArrowUpRight size={18} className="text-[#2E1A6B]" />
            </div>
          </motion.div>

          {/* Youth Voices + Community Heroes stacked */}
          <motion.div
            variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6, delay: 0.25 }}
            data-testid="section-youth-voices"
            className="md:col-span-8 relative min-h-[320px] md:min-h-[420px] overflow-hidden group"
          >
            <img src={IMG_YOUTH} alt="Youth voices" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/30 to-transparent" />
            <div className="relative z-10 h-full flex flex-col justify-between p-6 md:p-10 max-w-2xl">
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-semibold text-[#2AC1E0]">
                <Sparkles size={14} /> Youth Voices &amp; Community Heroes
              </div>
              <div>
                <h3 className="font-display text-white text-3xl md:text-5xl leading-[1.05] tracking-tight">
                  Written by young people. About young people. For young people.
                </h3>
                <p className="mt-4 text-white/85 text-sm md:text-base max-w-lg">
                  From peer educators to community changemakers — the stories that don&apos;t
                  make the front page, made loud here.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
